import { useEffect, useState, useRef, Dispatch, SetStateAction, useMemo, use } from "react";
import { DataContext } from "./DataContext"
import { ecucCataSelectObjTemplate, type ECUCataSelectObjectInterface } from "../../models/ECUCInterfaces";
import { saveDataToLocalStorage } from "../../utilities/localStorageUtils";
import { initializeFromStorage, isValidString, isValidArray, isValidObject, initializeFromLegacyStorage, isValidBoolean } from "../../utilities/persistOnPrefreshUtils";
import APIService from "../../APIs/APIService";
import type { ClusterSelectInterface } from "../../models/ClusterSelectInterfaces";
import API from "../../APIs/API";
import { ECUDataInterface, ECUDataTemplate } from "../../models/ECUData";
import { TempDataInterface, tempDataRefTemplate } from "../../models/TempDataRef";
import { ImportProcessData, importProcessDataTemplate } from "../../models/ImportProcessData";
import { SelectOption } from "../../utilities/dropDownOptions";
import { resolveProgramOptionsWithFallback } from "../../utilities/programOptionsUtils";

/**
 * Data provider component. Made to store the data state, loading state, and validation state of the app.
 * Can be retrieved by using the useDataContext hook.
 * @param {*} children : the children components
 * @returns the data provider component
 */

interface DataProviderProps { 
  children?: any,
  schema: any,
  setSchema: Dispatch<SetStateAction<any>>,
  ecuCataSelectObj: ECUCataSelectObjectInterface,
  setECUCataSelectObj: Dispatch<SetStateAction<ECUCataSelectObjectInterface>>
  dynamicSelectOptionsMap: Record<string, SelectOption[]>, setDynamicSelectOptionsMap: Dispatch<SetStateAction<Record<string, SelectOption[]>>>,
  apiService: APIService,
  api: API,
  formProcessingState: string,
  setFormProcessingState: Dispatch<SetStateAction<string>>,
  signingAllowedModules: string[],
  setSigningAllowedModules: Dispatch<SetStateAction<string[]>>
}

export function DataProvider({ 
  children, 
  schema, 
  setSchema,
  ecuCataSelectObj, 
  setECUCataSelectObj,
  dynamicSelectOptionsMap, setDynamicSelectOptionsMap,
  apiService,
  api,
  formProcessingState,
  setFormProcessingState,
  signingAllowedModules,
  setSigningAllowedModules
 }: Readonly<DataProviderProps>) {

    ///// State to hold architecture type
    const [architectureType, setArchitectureType] = useState<string>(() => 
      initializeFromStorage("architectureType", "", isValidString)
    );

    ///// Ref to hold data during ECUC fetching and processing
    const ECUData = useRef<ECUDataInterface>(
      initializeFromStorage("ECUData", structuredClone(ECUDataTemplate), isValidObject)
    );
    const setECUData = (updaterOrValue: ECUDataInterface | ((prevValue: ECUDataInterface) => ECUDataInterface)) => {
      const newState = typeof updaterOrValue === 'function' 
        ? updaterOrValue(ECUData.current) 
        : updaterOrValue;
      ECUData.current = newState;
      saveDataToLocalStorage("ECUData", newState);
    }

    ////// State to hold clusters rendering in ECUClusterSelect component
    const [clusterSelectObj, setClusterSelectObj] = useState<ClusterSelectInterface[]>(() => 
      initializeFromStorage("clusterSelectObj", [], isValidArray)
    );


    /////// Ref to hold temporary data that doesn't need to trigger re-renders
    const tempData = useRef<TempDataInterface>(
      initializeFromLegacyStorage("tempData", "backupSRPDState", structuredClone(tempDataRefTemplate), isValidObject)
    );

    const setTempData = (updaterOrValue: TempDataInterface | ((prevValue: TempDataInterface) => TempDataInterface)) => {
      const newState = typeof updaterOrValue === 'function' 
        ? updaterOrValue(tempData.current) 
        : updaterOrValue;
      tempData.current = newState;
      saveDataToLocalStorage("tempData", newState);
    }

    const resetDataProviderState = () => {
      // Reset all state to defaults
      setFormProcessingState("ECUC_SELECT");
      setArchitectureType("");
      setECUData(null);
      setClusterSelectObj([]);

      const fallbackProgramOptions = resolveProgramOptionsWithFallback(ecuCataSelectObj.programs);

      setECUCataSelectObj((prev) => ({
        ...structuredClone(ecucCataSelectObjTemplate),
        programs: {
          ...prev.programs,
          options: structuredClone(fallbackProgramOptions),
          unfilteredOptions: structuredClone(fallbackProgramOptions),
          value: []
        }
      }));
      setTempData(structuredClone(tempDataRefTemplate));
      
      // Clear localStorage
      const keysToRemove = [
        "formProcessingState", "architectureType",
        "signingAllowedModules", "ECUData", "dynamicSelectOptionsMap",
        "clusterSelectObj", "ecuCataSelectObj"
      ];
      
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.warn(`Failed to remove ${key} from localStorage:`, error);
        }
      });
      
      console.log("DataProvider state and localStorage cleared");
    }


    ////// State to hold process data during import operations
    const [importProcessData, setImportProcessData] = useState<ImportProcessData>(() => 
      {
        if (formProcessingState === "ECUC_SELECT") {
          return structuredClone(importProcessDataTemplate);
        } 
        const result = initializeFromStorage("importProcessData", structuredClone(importProcessDataTemplate), isValidObject);
        if (!result.hasOwnProperty("enabled")) {
          // Handle legacy data without 'enabled' property
          const enabled = initializeFromStorage("importMode", false, isValidBoolean);
          localStorage.removeItem("importMode");
          result.enabled = enabled;
        }
        return result;
      }
    );



    const renderCount = useRef(0);

    useEffect(() => {
      renderCount.current += 1;
      console.log("DataProvider Render count", renderCount.current);
    });

// Save state changes to localStorage
  useEffect(() => {
    // Save all current state to localStorage whenever it changes
    saveDataToLocalStorage("ecuCataSelectObj", ecuCataSelectObj);
    saveDataToLocalStorage("architectureType", architectureType);
    saveDataToLocalStorage("signingAllowedModules", signingAllowedModules);
    saveDataToLocalStorage("dynamicSelectOptionsMap", dynamicSelectOptionsMap);
    saveDataToLocalStorage("clusterSelectObj", clusterSelectObj);
    saveDataToLocalStorage("formProcessingState", formProcessingState);
    saveDataToLocalStorage("importProcessData", importProcessData);
    
    console.log("DataProvider state saved to local storage:", {
      formProcessingState,
      architectureType,
      signingAllowedModules,
      hasECUData: !!ECUData,
      clusterCount: clusterSelectObj.length,
      importProcessData,
    });
  }, [ecuCataSelectObj, architectureType, signingAllowedModules, ECUData, dynamicSelectOptionsMap, clusterSelectObj, formProcessingState, importProcessData]);

  const contextValue = useMemo(() => ({
    schema,
    ecuCataSelectObj,
    setECUCataSelectObj,
    apiService,
    api,
    formProcessingState,
    setFormProcessingState,
    architectureType,
    setArchitectureType,
    signingAllowedModules,
    setSigningAllowedModules,
    ECUData,
    setECUData,
    dynamicSelectOptionsMap,
    setDynamicSelectOptionsMap, 
    clusterSelectObj,
    setClusterSelectObj,
    resetDataProviderState,
    importProcessData,
    setImportProcessData,
    tempData,
    setTempData
  }), [
    schema,
    ecuCataSelectObj,
    setECUCataSelectObj,
    apiService,
    api,
    formProcessingState,
    setFormProcessingState,
    architectureType,
    setArchitectureType,
    signingAllowedModules,
    setSigningAllowedModules,
    ECUData,
    setECUData,
    dynamicSelectOptionsMap,
    setDynamicSelectOptionsMap,
    clusterSelectObj,
    setClusterSelectObj,
    resetDataProviderState,
    importProcessData,
    setImportProcessData,
    tempData,
    setTempData
  ]);


  return (
    <DataContext.Provider value={contextValue}>
      {children}
    </DataContext.Provider>
  )
}